/**
 * „E GATA", ANNOUNCED THE SAME WAY WHEREVER THE GUEST HAPPENS TO BE.
 *
 * Two screens need this and they must not drift apart: the order page
 * (OrderStatus.tsx), and the MENU (App.tsx's watcher), because a guest who has
 * ordered usually taps „Înapoi la meniu" and reads about the house while they
 * wait — which is exactly where the news has to reach them.
 *
 * The three tiers, in the order they degrade:
 *   1. sound + vibration — every browser, no permission, no opt-in;
 *   2. an IN-PAGE pop-up — drawn by the caller, because only the caller knows
 *      what its screen looks like; this module returns and the caller renders;
 *   3. a SYSTEM notification — only when the page is hidden and only when the
 *      guest granted permission. A banner over a page the guest is already
 *      looking at is noise; the pop-up is the notification there.
 *
 * On top of all three rides real Web Push (worker/push/webpush.ts), which is
 * the only tier that survives the tab being closed. Nothing here depends on it.
 */

/**
 * ONE AudioContext FOR THE LIFE OF THE PAGE, module-scoped rather than per
 * component. Chrome and WebKit cap concurrent contexts at around six, and the
 * staff app learned this the hard way: a context per event went silent on the
 * sixth chime of the evening — which is precisely when people stop watching
 * the screen and start trusting the sound.
 */
let ctx: AudioContext | null = null;

/**
 * Unlock the chime from inside a user gesture.
 *
 * A context created outside one starts `suspended` under the autoplay policy
 * and never recovers on its own, so the tap that asks for notifications also
 * warms this up. Safe to call repeatedly.
 */
export function primeChime(): void {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === "suspended") void ctx.resume();
  } catch {
    /* no audio on this device — the vibration and the pop-up still land */
  }
}

/**
 * UNLOCK THE CHIME ON THE FIRST TOUCH OF THE PAGE, whatever that touch is.
 *
 * The autoplay policy only lets an AudioContext start inside a user gesture,
 * and the only gesture that used to prime one was the „Anunță-mă când e gata"
 * tap. A guest who never taps it — most guests — reached „E gata" with a
 * context that had never been allowed to exist, so the sound was silent and
 * the moment passed with nothing but a colour change.
 *
 * Scrolling a menu, tapping a category, opening the basket: any of them is a
 * gesture, and one is enough. `once: true` on each, so this costs one listener
 * that removes itself.
 */
export function primeChimeOnFirstGesture(): void {
  const unlock = () => primeChime();
  for (const evt of ["pointerdown", "touchstart", "keydown"] as const) {
    window.addEventListener(evt, unlock, { once: true, passive: true });
  }
}

/**
 * THE SOUND, REDESIGNED TO BE HEARD.
 *
 * The old one was a single 880 Hz sine at gain 0.07 for 0.18s — a polite
 * "blip" that a phone speaker in a pocket, in a garden, with people talking,
 * does not deliver. It was borrowed from the staff tablet, where it announces
 * a new order to somebody already standing over the screen; the guest's case
 * is the opposite of that.
 *
 * What replaced it, and why each part:
 *   * A RISING THREE-NOTE FIGURE (B5 · E6 · B6) rather than one tone. A pitch
 *     that MOVES reads as a signal; a flat tone reads as interface noise, and
 *     the ear stops reporting it after a few evenings.
 *   * PLAYED TWICE, ~0.8s apart. One pattern is missable in a moment of
 *     laughter; two is unmistakable, and a third would be an alarm clock.
 *   * TRIANGLE, not sine. Its harmonics are what survive a small speaker —
 *     a pure sine at 1–2 kHz loses most of its energy on phone hardware.
 *   * Centred at 1–2 kHz, where phone speakers are actually efficient and
 *     where human hearing is most sensitive; the old note sat below that.
 *   * Gain 0.30 with a fast attack and an exponential tail, four times the
 *     old level but still short of harsh. The ramp matters: a square-edged
 *     start or stop produces an audible click on cheap hardware.
 */
function chime(): void {
  try {
    ctx ??= new AudioContext();
    const audio = ctx;

    // B5, E6, B6 — a rising fourth then an octave. Deliberately not a chord
    // from the house's own music; this has one job, which is to interrupt.
    const figure: { hz: number; at: number; len: number }[] = [
      { hz: 987.77, at: 0, len: 0.14 },
      { hz: 1318.51, at: 0.14, len: 0.14 },
      { hz: 1975.53, at: 0.28, len: 0.42 },
    ];

    const play = (offset: number) => {
      for (const note of figure) {
        const start = audio.currentTime + offset + note.at;
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(note.hz, start);
        // 8ms attack, then decay to silence. Never ramp to exactly 0 —
        // exponentialRampToValueAtTime refuses a zero target.
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.3, start + 0.008);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + note.len);
        osc.connect(gain).connect(audio.destination);
        osc.start(start);
        osc.stop(start + note.len + 0.02);
      }
    };

    const schedule = () => {
      play(0);
      play(0.8);
    };

    /*
     * A CHIME IS ONLY A CHIME AT THE RIGHT MOMENT.
     *
     * A context that has never been inside a user gesture is `suspended`, and
     * notes scheduled on it do not vanish — they FREEZE, and detonate at the
     * guest's next tap, whenever that is. Measured on a real order: the ready
     * sound fired when „Am înțeles" was pressed, minutes after the moment it
     * was meant to mark. A sound at the wrong moment is worse than none — the
     * vibration and the system notification carry the news without needing a
     * gesture at all.
     *
     * So: running → play now. Suspended → give `resume()` a few seconds, for
     * the guest whose thumb is landing right as the order turns ready — and
     * past the deadline, drop the sound entirely rather than store it up.
     */
    if (audio.state === "running") {
      schedule();
      return;
    }
    let expired = false;
    const deadline = setTimeout(() => {
      expired = true;
    }, 4000);
    void audio.resume().then(() => {
      if (expired) return;
      clearTimeout(deadline);
      schedule();
    });
  } catch {
    /* no audio on this device — the vibration and the pop-up still land */
  }
}

/**
 * Fire everything that is not on screen: vibration, chime, and — only for a
 * page the guest is NOT looking at — a system notification.
 *
 * `tag` is shared with the Worker's Web Push payload (`prispa-<orderId>`), so
 * a phone that receives both paths collapses them into ONE banner rather than
 * stacking two for the same drink.
 */
export function announceReady(opts: {
  orderId: string;
  pickupCode: string;
  title: string;
  body: string;
}): void {
  // Longer and more insistent than the staff tablet's polite double-buzz:
  // this one has to be felt through a pocket, not noticed on a counter.
  if ("vibrate" in navigator) navigator.vibrate?.([200, 100, 200, 100, 400]);
  chime();

  /*
   * THE BANNER FIRES EVEN WHEN THE PAGE IS IN FRONT OF THE GUEST.
   *
   * It used to return early unless `document.hidden`, on the reasoning that a
   * system banner over a page the guest is already reading is noise. Tested on
   * a real order, that reasoning was wrong in the case that matters most: the
   * guest sitting ON their order page, watching it, is exactly who expects the
   * phone to announce the moment — and a page that quietly changes a line of
   * text is not an announcement.
   *
   * The in-page pop-ups (menu AND order page) still carry the news for anyone
   * who has not granted permission; this is the tier above them, not instead.
   */
  if (!("Notification" in window) || Notification.permission !== "granted") return;

  /*
   * ONE BANNER PER DRINK. `prispa.notify.<id>` === "on" means a push
   * subscription is CONFIRMED on the server (OrderStatus.tsx writes it only
   * after the server said yes) — so the server's own banner is coming for
   * this exact moment, and this local one must stand down. The shared `tag`
   * was supposed to make double delivery harmless — same tag, one banner —
   * and on a real iPhone it is not: WebKit ignores web-push tag replacement,
   * so a guest with push on read the server's „Comanda e gata!" stacked over
   * this page's own wording, two notifications for one drink. Where push is
   * "page"-tier or off, this local banner is the only one there is and fires
   * exactly as before. The vibration and the chime above are not banners and
   * still mark the moment either way.
   */
  if (localStorage.getItem(`prispa.notify.${opts.orderId}`) === "on") return;

  const tag = `prispa-${opts.orderId}`;
  const url = `/o/${opts.orderId}`;
  void (async () => {
    try {
      // Android Chrome refuses `new Notification()` from a page and demands
      // the service-worker registration, so that path is tried first.
      const reg =
        "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
      if (reg) {
        await reg.showNotification(opts.title, { body: opts.body, tag, data: { url } });
        return;
      }
    } catch {
      /* fall through to the constructor */
    }
    try {
      new Notification(opts.title, { body: opts.body, tag });
    } catch {
      /* neither path available — the in-page pop-up is the whole story */
    }
  })();
}

/**
 * Has this order already had its moment on this phone?
 *
 * The POP-UP should reappear on a reload — the drink is still uncollected and
 * the code is still needed — but the SOUND must not: a guest who reopens the
 * menu three times while walking to the bar should not be chimed at three
 * times. So the noise is once per order, remembered across reloads, while the
 * banner is governed by the guest dismissing it.
 */
export function alertAlreadyFired(orderId: string): boolean {
  return localStorage.getItem(`prispa.readyalert.${orderId}`) === "1";
}

export function markAlertFired(orderId: string): void {
  try {
    localStorage.setItem(`prispa.readyalert.${orderId}`, "1");
  } catch {
    /* quota — worst case the guest hears it twice */
  }
}

/**
 * „AM ÎNȚELES" MEANS IT, AND SURVIVES A RELOAD.
 *
 * This was component state at first, on the reasoning that dismissal means "I
 * have read this" rather than "never again", so a reload while the drink was
 * still on the counter should put the code back. Wrong, per the owner: a guest
 * who has acknowledged the news and gone back to reading the menu should not
 * have it thrown at them again by a stray refresh or a tab restore. The code
 * has not gone anywhere — „Comenzile mele" in the header is one tap away.
 */
export function readyDismissed(orderId: string): boolean {
  return localStorage.getItem(`prispa.readyseen.${orderId}`) === "1";
}

export function markReadyDismissed(orderId: string): void {
  try {
    localStorage.setItem(`prispa.readyseen.${orderId}`, "1");
  } catch {
    /* quota — worst case the pop-up returns once */
  }
}
